import React from "react";
import PropTypes from "prop-types";
import {
  PredictionFeedback,
  ColorSelectQuestionContent,
} from "@learningblocks/storybook-components";
import { LogEvent } from "@learningblocks/shared-components";
import { QuestionWrapper } from "../Components/QuestionWrapper";
import { MainTitle } from "../Components/MainTitle";
import { Footer } from "../Components/Footer";
import { SubmitButton } from "../Components/SubmitButton";
import { ResetButton } from "../Components/ResetButton";
import "./style.scss";

const FEEDBACK = {
  SUCCESS: "SUCCESS",
  FAIL: "FAIL",
};
/**
 * ColorPrediction
 *  - Question Component to select a color for each solution
 */
export function ColorPrediction(props) {
  const getFeedbackProps = () => {
    if (props.feedbackType === FEEDBACK.SUCCESS) {
      return {
        type: FEEDBACK.SUCCESS,
        message: "Prediction Submitted!",
        className: "colorPredictionQuestion__feedback",
      };
    } else if (props.feedbackType === FEEDBACK.FAIL) {
      return {
        type: FEEDBACK.FAIL,
        message: "Select a color for all options.",
        className: "colorPredictionQuestion__feedback",
      };
    } else {
      return { type: "" };
    }
  };

  const onColorSelect = (params) => {
    props.onColorSelect(params);
  };

  const wrapperClass = ` colorPredictionQuestion ${props.className}`;

  return (
    <QuestionWrapper className={wrapperClass}>
      <MainTitle
        title={props.headTitle}
        className="colorPredictionQuestion__mainTitle"
      />
      <p>{props.text}</p>
      <ColorSelectQuestionContent
        question={props.question}
        options={props.options}
        onColorSelect={onColorSelect}
      />
      <PredictionFeedback {...getFeedbackProps()} />
      <Footer className="colorPredictionQuestion__footer">
        <ResetButton
          disabled={!props.submitted}
          onReset={props.onReset}
          className="colorPredictionQuestion__footer__reset"
        />
        <LogEvent
          logEventProps={{
            event_type: "sim_prediction_question clicked",
            event_schema: "track",
          }}
          actionProps={{ onSubmit: { action: "click" } }}
          elementType="button"
        >
          <SubmitButton
            disabled={props.submitted}
            onSubmit={props.onSubmit}
            label="Submit prediction"
            className="colorPredictionQuestion__footer__submit"
          />
        </LogEvent>
      </Footer>
    </QuestionWrapper>
  );
}

ColorPrediction.propTypes = {
  /**
   * Title to display on top
   */
  headTitle: PropTypes.string,
  /**
   * Question text
   */
  question: PropTypes.string,
  /**
   * Options for the question
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Label for option
       */
      optionLabel: PropTypes.string.isRequired,
      /**
       * Data needed to render optionTube
       */
      optionTube: PropTypes.shape({
        /**
         * Type of the tube Image
         */
        type: PropTypes.oneOf(["TUBE", "GLASS_TUBE"]).isRequired,
      }),
      /**
       * List of Checkboxes to display for option
       */
      checkboxes: PropTypes.arrayOf(PropTypes.object).isRequired,
      /**
       * data to show feedback
       */
      feedback: PropTypes.shape({
        /**
         * Boolean to set if feedback should be displayed
         */
        showFeedback: PropTypes.bool.isRequired,
        /**
         * Boolean to set if feedback should be correct/incorrect state
         */
        isCorrect: PropTypes.bool,
        /**
         * Message to display on feedback
         */
        message: PropTypes.string,
      }).isRequired,
    })
  ),
  /**
   * Type of feedback to show
   */
  feedbackType: PropTypes.oneOf(["SUCCESS", "FAIL", ""]),
  /**
   * Function to execute when color selected in checkbox
   */
  onColorSelect: PropTypes.func,
  /**
   * Funtion to execute on click Reset Button
   */
  onReset: PropTypes.func,
  /**
   * Functon to execute on click Submit Button
   */
  onSubmit: PropTypes.func,
  /**
   * className to be set for ColorSelection
   */
  className: PropTypes.string,
};

ColorPrediction.defaultProps = {
  headTitle: "Prediction Question",
  feedbackType: "",
  className: "",
};
